import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useState, useRef } from "react";
import {
  ArrowRight, ChevronRight, ChevronDown, ChevronUp, Mail,
  Brain, Database, Target, BarChart3, Shield, Cpu, Zap, Lock, Server, Layers,
  MessageSquare, Sparkles, GitBranch, Activity, Eye, Bell, FileSearch,
  Headphones, GraduationCap, Users, Briefcase, ShoppingBag,
  Clock, AlertTriangle, TrendingUp, Workflow, Network,
} from "lucide-react";
import heroBg from "@/assets/hero-bg.jpg";
import { SyraLogo } from "@/components/brand/SyraLogo";

/* ─── Animation ─── */
const ease = [0.16, 1, 0.3, 1] as const;
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.55, ease } }),
};

/* ─── Data ─── */
const PAIN_POINTS = [
  {
    icon: AlertTriangle,
    title: "O scroll engole tudo",
    desc: "Decisões importantes somem em 3 dias. Quem entrou ontem nunca vai achar a thread que importa.",
  },
  {
    icon: Clock,
    title: "Você vira o gargalo",
    desc: "Cada 'qual é o link?', 'quem ficou de fazer?', 'cadê aquele áudio?' aterrissa no seu celular.",
  },
  {
    icon: Eye,
    title: "Zero visibilidade do que rola",
    desc: "Não sabe quem engaja, quem está prestes a sair, quem está pronto pra comprar. É tudo achismo.",
  },
];

const FEATURES = [
  {
    icon: Brain,
    title: "Memória que não esquece",
    tagline: "5 camadas, embeddings vetoriais",
    desc: "STM, LTM episódica, semântica, procedural e working memory. A SYRA lembra do que você falou em janeiro quando você perguntar em julho.",
  },
  {
    icon: MessageSquare,
    title: "Resumos sob demanda",
    tagline: "'@syra resume as últimas 200 msgs'",
    desc: "Pulou o dia? Pede um resumo. Por pessoa, por tópico, por janela de tempo. Direto no grupo, sem dashboard.",
  },
  {
    icon: Target,
    title: "Skills modulares",
    tagline: "Skill OS v19 com A/B testing",
    desc: "Triagem, tradução, transcrição de áudio, descrição de imagem, busca externa, lembretes. Liga e desliga por grupo.",
  },
  {
    icon: GitBranch,
    title: "Roteamento multi-modelo",
    tagline: "Gemini, GPT-5, Claude, Lovable AI",
    desc: "Cada turno escolhe o modelo certo: rápido para resposta curta, raciocínio para conflito, multimodal para mídia. Com circuit breaker e fallback.",
  },
  {
    icon: Sparkles,
    title: "Theory of Mind + Affect",
    tagline: "Lê tom, contexto e quem fala",
    desc: "Sabe diferenciar tirar sarro de provocar briga. Modula o estilo da resposta com base no estado emocional do grupo.",
  },
  {
    icon: Bell,
    title: "Compromissos e lembretes",
    tagline: "Extrai de conversa natural",
    desc: "'Mando até sexta', 'me lembra da reunião' viram tarefas rastreáveis. SYRA cobra de volta no prazo.",
  },
  {
    icon: FileSearch,
    title: "Multimodal de verdade",
    tagline: "Áudio, imagem, vídeo, link",
    desc: "Transcreve áudios, descreve imagens, abre links sociais (Instagram, YouTube, TikTok, X). Persiste mídia entre turnos.",
  },
  {
    icon: BarChart3,
    title: "Inteligência operacional",
    tagline: "Churn, propensão, scoring",
    desc: "Quem está esfriando, quem está quente para comprar, quem é influenciador silencioso. Métricas que valem dinheiro.",
  },
  {
    icon: Shield,
    title: "Sentinel & auditoria",
    tagline: "Tudo rastreável por turno",
    desc: "Cada decisão da IA tem snapshot completo: contexto, modelo, prompt, resposta. Reverte, audita, melhora.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Conecta o WhatsApp",
    desc: "QR code via Evolution API. Escolhe os grupos. 2 minutos.",
    icon: Network,
  },
  {
    step: "02",
    title: "Define o modo de cada grupo",
    desc: "Silencioso (só observa), Menção (responde @syra), Autônomo (participa proativo).",
    icon: Workflow,
  },
  {
    step: "03",
    title: "A inteligência aparece sozinha",
    desc: "Resumos, alertas, CRM e relatórios chegam no painel sem você pedir.",
    icon: Activity,
  },
];

const USE_CASES = [
  {
    icon: GraduationCap,
    persona: "Infoproduto / curso online",
    desc: "Suporte 24/7 com base de conhecimento, alunos em risco identificados antes de sumir, FAQ que responde sozinha.",
    metric: "−70% no tempo de resposta",
  },
  {
    icon: Users,
    persona: "Comunidade B2B",
    desc: "Engajamento por membro, top contribuidores, relatório de saúde mensal automatizado para stakeholders.",
    metric: "Visibilidade total",
  },
  {
    icon: Headphones,
    persona: "Suporte / atendimento",
    desc: "Triagem automática, tickets com SLA, macros, CSAT. Resolve mais com menos gente, sem perder qualidade.",
    metric: "3x mais throughput",
  },
  {
    icon: Briefcase,
    persona: "Comercial / vendas",
    desc: "Score de propensão, sinais de buy intent, lembretes de follow-up extraídos da conversa.",
    metric: "Mais conversão",
  },
  {
    icon: ShoppingBag,
    persona: "E-commerce / marketplace",
    desc: "Captura pedidos no chat, identifica reclamações recorrentes, alerta quando algo está estourando.",
    metric: "NPS em tempo real",
  },
  {
    icon: GraduationCap,
    persona: "Mentoria / coletivo",
    desc: "Memória do que cada mentorado disse, follow-up automático, padrões de evolução por participante.",
    metric: "Personalização em escala",
  },
];

const TECH_PROOF = {
  stack: ["Lovable Cloud", "pgvector", "Evolution API", "Multi-LLM"],
  differentials: [
    { label: "Multi-tenant com RLS por org", icon: Shield },
    { label: "LGPD-ready, dados isolados", icon: Lock },
    { label: "Circuit breaker por provider", icon: Zap },
    { label: "Auditoria completa por turno", icon: FileSearch },
    { label: "Telemetria contínua de qualidade", icon: Activity },
    { label: "Policy learner que evolui sozinho", icon: Brain },
  ],
};

const PLANS_SUMMARY = [
  { name: "Free", price: "0", msgs: "500 msgs/mês", desc: "Pra testar a real", highlight: false },
  { name: "Starter", price: "197", msgs: "5.000 msgs/mês", desc: "Pra começar pra valer", highlight: false },
  { name: "Pro", price: "497", msgs: "25.000 msgs/mês", desc: "Pra escalar sem dor", highlight: true, badge: "Mais escolhido" },
  { name: "Business", price: "997", msgs: "100.000 msgs/mês", desc: "Pra times sérios", highlight: false },
];

const FAQ_ITEMS = [
  { q: "Como SYRA é diferente de um chatbot?", a: "Chatbot responde pergunta. SYRA extrai conhecimento de conversa, lembra entre sessões, roteia entre modelos de IA, gera relatórios, identifica padrões e aprende com o uso. É infraestrutura cognitiva, não fluxo de árvore de decisão." },
  { q: "Como conecta no WhatsApp?", a: "Via Evolution API: você escaneia um QR code no wizard de onboarding e seleciona quais grupos quer gerenciar. Leva menos de 2 minutos. Sem WhatsApp Business API, sem aprovação Meta, sem espera." },
  { q: "Os dados são seguros?", a: "Isolamento total entre organizações via Row Level Security no Postgres. Criptografia em trânsito e em repouso. LGPD-ready com ferramentas nativas de exclusão e portabilidade. Auditoria completa de cada acesso." },
  { q: "Posso testar antes de pagar?", a: "Plano Free é permanente, sem cartão. Planos pagos têm 14 dias de trial gratuito. Cancela quando quiser, do próprio painel." },
  { q: "A SYRA responde sozinha nos grupos?", a: "Você decide por grupo: Silencioso (só observa e gera relatórios), Menção (responde quando citada), ou Autônomo (participa quando faz sentido). Nada de comportamento padrão indesejado." },
  { q: "Quantos grupos posso gerenciar?", a: "De 2 (Free) a 200+ (Business). Operações maiores têm plano Enterprise com SLA, integrações customizadas e contrato dedicado." },
  { q: "Funciona em qualquer modelo de IA?", a: "Roteia entre Gemini, GPT-5, Claude, Lovable AI e provedores próprios via BYOK. Cada turno escolhe o melhor modelo para o contexto, com fallback automático se algum provider falhar." },
];

/* ─── Shared Components ─── */
function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 rounded-2xl overflow-hidden backdrop-blur-sm bg-white/[0.03] hover:bg-white/[0.05] transition-colors">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left">
        <span className="font-semibold text-sm sm:text-base pr-4 text-white/90">{q}</span>
        {open ? <ChevronUp className="h-5 w-5 text-white/40 shrink-0" /> : <ChevronDown className="h-5 w-5 text-white/40 shrink-0" />}
      </button>
      {open && <div className="px-5 pb-5 text-sm text-white/60 leading-relaxed">{a}</div>}
    </div>
  );
}

function GlowCard({ children, className = "", highlight = false }: { children: React.ReactNode; className?: string; highlight?: boolean }) {
  return (
    <div className={`relative group ${className}`}>
      {highlight && (
        <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-b from-cyan-500/40 via-blue-500/25 to-purple-500/15 blur-sm opacity-80 group-hover:opacity-100 transition-opacity" />
      )}
      <div className={`relative rounded-2xl border backdrop-blur-sm transition-all duration-300 h-full ${
        highlight
          ? "bg-white/[0.08] border-cyan-500/30 group-hover:border-cyan-400/60"
          : "bg-white/[0.04] border-white/[0.08] group-hover:border-white/20 group-hover:bg-white/[0.06]"
      }`}>
        {children}
      </div>
    </div>
  );
}

/* ─── Chat Demo (mock visual) ─── */
function ChatDemo() {
  return (
    <div className="relative max-w-md mx-auto">
      <div className="absolute -inset-4 bg-gradient-to-tr from-cyan-500/20 via-blue-500/10 to-purple-500/20 blur-2xl rounded-[2rem]" />
      <div className="relative rounded-3xl border border-white/10 bg-[#0a1020]/95 backdrop-blur-xl shadow-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.06] bg-white/[0.02]">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
            <Users className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm text-white/90">Comunidade Pro · 84 membros</div>
            <div className="text-[11px] text-white/40">SYRA ativa · modo autônomo</div>
          </div>
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
        </div>

        <div className="p-5 space-y-3 text-sm">
          <Bubble who="Marina" color="purple">qual era o link daquele material que a Ana mandou semana passada?</Bubble>
          <BubbleSyra>
            achei aqui — Ana compartilhou no dia 18, planilha de funil de vendas:<br />
            <span className="text-cyan-300 underline">drive.google.com/funil-q4</span>
          </BubbleSyra>

          <Bubble who="Caio" color="amber">@syra resume o que rolou hoje em 5 bullets</Bubble>
          <BubbleSyra>
            <span className="font-semibold text-white/90 block mb-1.5">Resumo de hoje:</span>
            • Decisão de migrar para o plano Pro (Marina, 14h)<br />
            • Bug no checkout reportado por 3 pessoas<br />
            • Reunião remarcada pra quinta 15h<br />
            • Ana ficou de mandar o briefing até sexta<br />
            • Novo membro entrou: Pedro (founder)
          </BubbleSyra>

          <Bubble who="Ana" color="emerald">tô fora do Brasil semana que vem, ninguém me cobra nada plmds 🙏</Bubble>
          <div className="text-[11px] text-white/30 pl-3 italic">SYRA registrou ausência e silenciou cobranças até dia 28</div>
        </div>

        <div className="px-5 py-3 border-t border-white/[0.06] bg-white/[0.02] flex items-center gap-2">
          <div className="flex-1 text-xs text-white/30">Digite uma mensagem...</div>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
            <ArrowRight className="w-3.5 h-3.5 text-white" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Bubble({ who, color, children }: { who: string; color: "purple" | "amber" | "emerald"; children: React.ReactNode }) {
  const colorMap = {
    purple: "text-purple-300",
    amber: "text-amber-300",
    emerald: "text-emerald-300",
  };
  return (
    <div className="flex flex-col items-start max-w-[90%]">
      <div className={`text-[10px] uppercase tracking-wide font-semibold mb-1 ${colorMap[color]}`}>{who}</div>
      <div className="bg-white/[0.06] border border-white/[0.08] rounded-2xl rounded-tl-sm px-3.5 py-2 text-white/80 text-[13px] leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function BubbleSyra({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-end max-w-[92%] ml-auto">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-[10px] uppercase tracking-wide font-semibold text-cyan-300">SYRA</span>
        <Sparkles className="w-2.5 h-2.5 text-cyan-300" />
      </div>
      <div className="bg-gradient-to-br from-cyan-500/15 to-blue-600/10 border border-cyan-500/20 rounded-2xl rounded-tr-sm px-3.5 py-2.5 text-white/90 text-[13px] leading-relaxed">
        {children}
      </div>
    </div>
  );
}

/* ─── Page ─── */
export default function Landing() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <div className="min-h-screen bg-[#060a14] text-white overflow-x-hidden" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* ═══ NAV ═══ */}
      <nav className="fixed top-0 inset-x-0 z-50 border-b border-white/[0.06]" style={{ background: "rgba(6,10,20,0.8)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2.5">
            <SyraLogo size={28} variant="mark" />
            <span className="text-lg font-bold tracking-[0.18em]" style={{ fontFamily: "'Manrope', sans-serif" }}>SYRA</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-white/50">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#how" className="hover:text-white transition-colors">Como funciona</a>
            <a href="#use-cases" className="hover:text-white transition-colors">Pra quem é</a>
            <a href="#pricing" className="hover:text-white transition-colors">Planos</a>
            <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10" onClick={() => navigate("/login")}>Entrar</Button>
            <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white border-0 shadow-lg shadow-cyan-500/20" onClick={() => navigate("/login")}>
              Começar grátis
            </Button>
          </div>
        </div>
      </nav>

      {/* ═══ 1. HERO ═══ */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden pt-24">
        <motion.div className="absolute inset-0" style={{ opacity: heroOpacity, scale: heroScale }}>
          <img src={heroBg} alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#060a14]/70 via-[#060a14]/60 to-[#060a14]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#060a14]/85 via-transparent to-[#060a14]/85" />
        </motion.div>
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center pb-20">
          {/* Lado esquerdo — copy */}
          <div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease }}>
              <Badge className="mb-6 text-xs px-3 py-1.5 gap-2 bg-cyan-500/10 border-cyan-500/20 text-cyan-300 hover:bg-cyan-500/15 backdrop-blur-sm">
                <Sparkles className="w-3 h-3" />
                Nova era de comunidade no WhatsApp
              </Badge>
            </motion.div>

            <motion.h1
              className="text-4xl sm:text-5xl lg:text-[4.2rem] font-extrabold tracking-tight leading-[1.02] mb-6"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6, ease }}
            >
              Seus grupos de WhatsApp já têm o ouro.{" "}
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                Falta alguém minerando.
              </span>
            </motion.h1>

            <motion.p
              className="text-lg sm:text-xl text-white/65 max-w-xl mb-8 leading-relaxed"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5, ease }}
            >
              SYRA é uma IA que vive nos seus grupos. Ela <span className="text-white font-semibold">lembra de tudo</span>, responde quando faz sentido,
              extrai compromissos, gera resumos sob demanda e te entrega <span className="text-white font-semibold">inteligência operacional</span> que
              o WhatsApp nunca vai dar.
            </motion.p>

            <motion.div className="flex flex-col sm:flex-row items-start gap-4 mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5, ease }}>
              <Button
                size="lg"
                onClick={() => navigate("/login")}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white border-0 text-base px-8 h-14 shadow-2xl shadow-cyan-500/25 hover:shadow-cyan-400/40 transition-all"
              >
                Começar agora — é grátis <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-base px-8 h-14 bg-white/[0.05] border-white/[0.15] text-white hover:bg-white/[0.1] hover:border-white/[0.25]"
                onClick={() => navigate("/contact")}
              >
                <Mail className="mr-2 h-4 w-4" /> Quero uma demo
              </Button>
            </motion.div>

            <motion.div
              className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/45"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            >
              <span className="flex items-center gap-1.5"><div className="w-1 h-1 rounded-full bg-emerald-400" /> 14 dias grátis</span>
              <span className="flex items-center gap-1.5"><div className="w-1 h-1 rounded-full bg-emerald-400" /> Sem cartão</span>
              <span className="flex items-center gap-1.5"><div className="w-1 h-1 rounded-full bg-emerald-400" /> Setup em 2 min</span>
              <span className="flex items-center gap-1.5"><div className="w-1 h-1 rounded-full bg-emerald-400" /> LGPD-ready</span>
            </motion.div>
          </div>

          {/* Lado direito — chat demo */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.7, ease }}
            className="hidden lg:block"
          >
            <ChatDemo />
          </motion.div>
        </div>
      </section>

      {/* ═══ 2. PROBLEMA ═══ */}
      <section className="py-24 px-4 sm:px-6 relative">
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-red-500/[0.025] blur-[120px]" />
        <div className="max-w-5xl mx-auto relative">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <Badge className="mb-4 text-xs bg-red-500/10 border-red-500/20 text-red-400">SE VOCÊ GERENCIA UM GRUPO ATIVO</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Provavelmente está perdendo{" "}
              <span className="text-red-400">tempo, dinheiro e gente</span>
              <br />sem nem perceber.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {PAIN_POINTS.map((p, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}>
                <GlowCard>
                  <div className="p-6 space-y-3">
                    <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                      <p.icon className="h-5 w-5 text-red-400" />
                    </div>
                    <h3 className="font-semibold text-white/90 text-lg" style={{ fontFamily: "'Manrope', sans-serif" }}>{p.title}</h3>
                    <p className="text-sm text-white/55 leading-relaxed">{p.desc}</p>
                  </div>
                </GlowCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 3. FEATURES ═══ */}
      <section id="features" className="py-24 px-4 sm:px-6 relative" style={{ background: "linear-gradient(180deg, rgba(6,10,20,1) 0%, rgba(10,16,32,1) 50%, rgba(6,10,20,1) 100%)" }}>
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] rounded-full bg-cyan-500/[0.03] blur-[120px]" />
        <div className="max-w-6xl mx-auto relative">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <Badge className="mb-4 text-xs bg-white/[0.06] border-white/[0.1] text-white/60">9 SUPERPODERES</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Não é mais um chatbot.{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">É um cérebro pro seu grupo.</span>
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto text-lg">Cada feature é resultado de meses de engenharia em IA conversacional, memória vetorial e roteamento multi-modelo.</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i % 3}>
                <GlowCard className="h-full">
                  <div className="p-6 space-y-3 h-full flex flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/15 flex items-center justify-center shrink-0">
                        <f.icon className="h-5 w-5 text-cyan-400" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-bold text-white/95 text-base" style={{ fontFamily: "'Manrope', sans-serif" }}>{f.title}</h3>
                      <p className="text-[11px] uppercase tracking-wide text-cyan-400/70 font-semibold">{f.tagline}</p>
                    </div>
                    <p className="text-sm text-white/55 leading-relaxed flex-1">{f.desc}</p>
                  </div>
                </GlowCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 4. COMO FUNCIONA ═══ */}
      <section id="how" className="py-24 px-4 relative">
        <div className="max-w-5xl mx-auto">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <Badge className="mb-4 text-xs bg-white/[0.06] border-white/[0.1] text-white/60">COMEÇA EM MINUTOS</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>
              3 passos. <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Zero código.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-14 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
            {HOW_IT_WORKS.map((item, i) => (
              <motion.div key={i} className="text-center relative" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}>
                <div className="relative inline-flex mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/20 flex items-center justify-center mx-auto">
                    <item.icon className="h-7 w-7 text-cyan-400" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-xs font-bold text-white">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-bold text-lg mb-2 text-white/90" style={{ fontFamily: "'Manrope', sans-serif" }}>{item.title}</h3>
                <p className="text-sm text-white/55 max-w-xs mx-auto leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 5. CASOS DE USO ═══ */}
      <section id="use-cases" className="py-24 px-4 relative" style={{ background: "linear-gradient(180deg, rgba(6,10,20,1) 0%, rgba(10,16,32,1) 50%, rgba(6,10,20,1) 100%)" }}>
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <Badge className="mb-4 text-xs bg-white/[0.06] border-white/[0.1] text-white/60">PRA QUEM É</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Se você vive de comunidade,{" "}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">SYRA é pra você.</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {USE_CASES.map((uc, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i % 3}>
                <GlowCard className="h-full">
                  <div className="p-6 space-y-4 h-full flex flex-col">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                      <uc.icon className="h-5 w-5 text-cyan-400" />
                    </div>
                    <h3 className="font-bold text-white/95" style={{ fontFamily: "'Manrope', sans-serif" }}>{uc.persona}</h3>
                    <p className="text-sm text-white/55 leading-relaxed flex-1">{uc.desc}</p>
                    <div className="pt-3 border-t border-white/[0.06] flex items-center gap-2">
                      <TrendingUp className="w-3.5 h-3.5 text-cyan-400" />
                      <span className="text-xs font-semibold text-cyan-300">{uc.metric}</span>
                    </div>
                  </div>
                </GlowCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 6. ARQUITETURA ═══ */}
      <section className="py-24 px-4 relative">
        <div className="max-w-5xl mx-auto">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <Badge className="mb-4 text-xs bg-white/[0.06] border-white/[0.1] text-white/60">FEITO PRA AGUENTAR</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Infraestrutura <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">enterprise-grade</span> desde o dia zero.
            </h2>
            <p className="text-white/50 max-w-xl mx-auto">Não é promessa de roadmap. É o que já está rodando em produção.</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
              <GlowCard>
                <div className="p-7">
                  <div className="flex items-center gap-2 mb-5">
                    <Server className="h-5 w-5 text-cyan-400" />
                    <h3 className="font-bold text-white/90" style={{ fontFamily: "'Manrope', sans-serif" }}>Stack</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {TECH_PROOF.stack.map((t) => (
                      <span key={t} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white/[0.06] border border-white/[0.08] text-white/65">{t}</span>
                    ))}
                  </div>
                </div>
              </GlowCard>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
              <GlowCard>
                <div className="p-7">
                  <div className="flex items-center gap-2 mb-5">
                    <Shield className="h-5 w-5 text-cyan-400" />
                    <h3 className="font-bold text-white/90" style={{ fontFamily: "'Manrope', sans-serif" }}>Diferenciais</h3>
                  </div>
                  <ul className="space-y-2.5">
                    {TECH_PROOF.differentials.map((d) => (
                      <li key={d.label} className="flex items-center gap-3 text-sm text-white/65">
                        <d.icon className="h-4 w-4 text-cyan-400/80 shrink-0" />
                        {d.label}
                      </li>
                    ))}
                  </ul>
                </div>
              </GlowCard>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 7. PRICING ═══ */}
      <section id="pricing" className="py-24 px-4 sm:px-6 relative" style={{ background: "linear-gradient(180deg, rgba(6,10,20,1) 0%, rgba(10,16,32,1) 50%, rgba(6,10,20,1) 100%)" }}>
        <div className="max-w-5xl mx-auto relative">
          <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <Badge className="mb-4 text-xs bg-white/[0.06] border-white/[0.1] text-white/60">PREÇO HONESTO</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Escolhe o tamanho. <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Cresce quando precisar.</span>
            </h2>
            <p className="text-white/55 text-lg">Sem letras miúdas. Sem fee de setup. Cancela do painel.</p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {PLANS_SUMMARY.map((plan, i) => (
              <motion.div key={plan.name} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}>
                <GlowCard highlight={plan.highlight} className="h-full">
                  <div className="p-6 flex flex-col items-center text-center relative h-full">
                    {plan.badge && (
                      <span className="absolute -top-3 px-3 py-1 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-[11px] font-semibold text-white whitespace-nowrap shadow-lg shadow-cyan-500/30">
                        {plan.badge}
                      </span>
                    )}
                    <h3 className="font-semibold text-lg text-white/90 mb-1" style={{ fontFamily: "'Manrope', sans-serif" }}>{plan.name}</h3>
                    <p className="text-[11px] text-white/40 mb-4">{plan.desc}</p>
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="text-sm text-white/40">R$</span>
                      <span className="text-3xl font-extrabold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>{plan.price}</span>
                      <span className="text-sm text-white/40">/mês</span>
                    </div>
                    <p className="text-xs text-white/45 mb-5">{plan.msgs}</p>
                    <Button
                      size="sm"
                      className={`w-full mt-auto ${plan.highlight
                        ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white border-0 shadow-lg shadow-cyan-500/20"
                        : "bg-white/[0.08] border-white/[0.12] text-white hover:bg-white/[0.15]"
                      }`}
                      variant={plan.highlight ? "default" : "outline"}
                      onClick={() => navigate("/login")}
                    >
                      {plan.price === "0" ? "Começar grátis" : "Trial 14 dias"}
                    </Button>
                  </div>
                </GlowCard>
              </motion.div>
            ))}
          </div>

          <motion.div className="text-center mt-8" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={4}>
            <Button
              variant="outline"
              className="bg-white/[0.05] border-white/[0.15] text-white hover:bg-white/[0.1]"
              onClick={() => navigate("/pricing")}
            >
              Comparar todos os planos <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ═══ 8. FAQ ═══ */}
      <section id="faq" className="py-24 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Perguntas <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">que todo mundo faz</span>
            </h2>
          </motion.div>
          <motion.div className="space-y-3" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
            {FAQ_ITEMS.map((item, i) => <FAQItem key={i} q={item.q} a={item.a} />)}
          </motion.div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-28 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/[0.06] via-blue-500/[0.10] to-purple-500/[0.06]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-cyan-500/[0.05] blur-[120px]" />
        <div className="max-w-3xl mx-auto text-center relative">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <Badge className="mb-6 text-xs px-3 py-1.5 bg-cyan-500/10 border-cyan-500/20 text-cyan-300">PRONTO?</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-6 leading-[1.05]" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Para de perder<br className="hidden sm:block" />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">o que importa.</span>
            </h2>
            <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto">Liga a SYRA hoje. Em 2 minutos seu grupo já está sendo organizado, lembrado e auditado por uma IA que não dorme.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                onClick={() => navigate("/login")}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white border-0 text-base px-10 h-14 shadow-2xl shadow-cyan-500/25"
              >
                Começar grátis agora <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/[0.05] border-white/[0.15] text-white hover:bg-white/[0.1]"
                onClick={() => navigate("/contact")}
              >
                <Mail className="mr-2 h-4 w-4" /> Falar com vendas
              </Button>
            </div>
            <p className="text-xs text-white/40 mt-6">Sem cartão · Cancela quando quiser · Suporte humano em PT-BR</p>
          </motion.div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="border-t border-white/[0.06] py-14 px-4" style={{ background: "rgba(6,10,20,0.95)" }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <SyraLogo size={22} variant="mark" />
                <span className="font-bold tracking-[0.18em]" style={{ fontFamily: "'Manrope', sans-serif" }}>SYRA</span>
              </div>
              <p className="text-xs text-white/40 leading-relaxed">A IA que vive nos seus grupos do WhatsApp. Lembra, aprende, organiza e te entrega inteligência operacional.</p>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-3 text-white/70">Produto</h4>
              <ul className="space-y-2 text-xs text-white/40">
                <li><a href="#features" className="hover:text-white/70 transition-colors">Features</a></li>
                <li><a href="#how" className="hover:text-white/70 transition-colors">Como funciona</a></li>
                <li><a href="#pricing" className="hover:text-white/70 transition-colors">Planos</a></li>
                <li><span className="cursor-pointer hover:text-white/70 transition-colors" onClick={() => navigate("/pricing")}>Comparar planos</span></li>
                <li><span className="cursor-pointer hover:text-white/70 transition-colors" onClick={() => navigate("/blog")}>Blog</span></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-3 text-white/70">Empresa</h4>
              <ul className="space-y-2 text-xs text-white/40">
                <li><a href="#faq" className="hover:text-white/70 transition-colors">FAQ</a></li>
                <li><span className="cursor-pointer hover:text-white/70 transition-colors" onClick={() => navigate("/contact")}>Contato</span></li>
                <li><span className="cursor-pointer hover:text-white/70 transition-colors" onClick={() => navigate("/status")}>Status</span></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-3 text-white/70">Legal</h4>
              <ul className="space-y-2 text-xs text-white/40">
                <li><span className="hover:text-white/70 transition-colors cursor-pointer" onClick={() => navigate("/privacy")}>Política de Privacidade</span></li>
                <li><span className="hover:text-white/70 transition-colors cursor-pointer" onClick={() => navigate("/privacy")}>LGPD</span></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/30">© {new Date().getFullYear()} SYRA. Todos os direitos reservados.</p>
            <p className="text-xs text-white/20">Construído pra quem leva comunidade a sério.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
